import React, { useState, memo, useEffect } from 'react';
import { ClipboardList } from 'lucide-react';
import { updateCaseStatus } from '../../services/addService.service';
import { formatDateTime } from '../../utils/dateUtils';
import { Section, Field } from './workspacePrimitives';

const CaseDetailsSegment = ({ caseObj, onOpenCustomer }) => {
  const [caseStatus, setCaseStatus] = useState(caseObj.case?.status || 'open');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const originalStatus = caseObj.case?.status || 'open';

  useEffect(() => {
    setCaseStatus(caseObj.case?.status || 'open');
  }, [caseObj.case?.id, caseObj.case?.status]);

  const caseStatusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const customerName = caseObj.customer?.name || 'Customer Name';
  const createdBy = caseObj.created_by?.name || 'N/A';
  const causeOfCall = caseObj.scheme_cause_of_call?.name || 'N/A';
  const formatCaseLocation = (loc) => {
    if (!loc) return null;
    if (typeof loc.address === 'string' && loc.address.trim()) return loc.address.trim();
    if (loc.address && typeof loc.address === 'object') {
      const nested =
        loc.address.address ||
        loc.address.formatted_address ||
        loc.address.full_address ||
        [loc.address.landmark, loc.address.city, loc.address.state].filter(Boolean).join(', ');
      if (nested && String(nested).trim()) return String(nested).trim();
    }
    if (loc.latitude != null && loc.longitude != null) {
      return `${loc.latitude}, ${loc.longitude}`;
    }
    return null;
  };
  const location = formatCaseLocation(caseObj.case?.location) || 'Not captured';
  const vehicleRegnNo = caseObj.vehicle?.registration_number || 'N/A';
  const contactNo = caseObj.customer?.mobile_number || 'N/A';
  const caseCreationDate = caseObj.case?.created_at ? formatDateTime(caseObj.case.created_at) : 'N/A';
  const lastServiceCreationDate = caseObj.case?.last_service_creation_date
    ? formatDateTime(caseObj.case.last_service_creation_date)
    : 'N/A';
  const vehicleModel = caseObj.vehicle?.model || 'N/A';
  const incidentType = caseObj.scheme_cause_of_call?.name || 'N/A';

  const validateCaseClosure = async () => {
    try {
      const services = caseObj.services || [];
      if (services.length === 0) {
        return true;
      }
      return services.every(
        (service) => service.status === 'service_complete' || service.status === 'service_cancelled'
      );
    } catch (error) {
      console.error('Error validating case closure:', error);
      return false;
    }
  };

  const handleSaveCase = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      if (caseStatus === 'closed') {
        const canClose = await validateCaseClosure();
        if (!canClose) {
          setCaseStatus(originalStatus);
          setSaveMessage('Cannot close case: All services must be completed or cancelled first.');
          setTimeout(() => {
            setSaveMessage('');
          }, 5000);
          return;
        }
      }

      await updateCaseStatus(caseObj.case?.id, caseStatus, null);

      setSaveMessage('Case status updated successfully!');
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving case:', error);
      setSaveMessage(error.response?.data?.detail || 'Failed to update case status');
      setTimeout(() => {
        setSaveMessage('');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const headerAction = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2">
        <span className="cwp-label-eyebrow whitespace-nowrap text-slate-500">Case status</span>
        <select
          className="cwp-field min-w-[8.5rem] py-1.5 text-[13px]"
          value={caseStatus}
          onChange={(e) => setCaseStatus(e.target.value)}
        >
          {caseStatusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={handleSaveCase}
        disabled={isSaving}
        className="cwp-btn-primary px-4 py-2 text-[13px] disabled:opacity-50"
      >
        {isSaving ? 'Saving…' : 'Save case'}
      </button>
    </div>
  );

  return (
    <Section
      icon={<ClipboardList className="h-3.5 w-3.5" strokeWidth={2} />}
      title="Case overview"
      subtitle="Incident, vehicle, and timeline metadata"
      dense
      action={headerAction}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Customer</span>
        <button
          type="button"
          onClick={() => onOpenCustomer?.()}
          className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
        >
          {customerName}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        <Field
          label="Cause of call"
          value={
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-hidden />
              {causeOfCall}
            </span>
          }
        />
        <Field
          label="Type of fault"
          value={
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-hidden />
              {incidentType}
            </span>
          }
        />
        <Field
          label="Location"
          value={<span className={location === 'Not captured' ? 'text-slate-500' : ''}>{location}</span>}
        />
        <Field label="Vehicle reg." value={vehicleRegnNo} mono />
        <Field label="Vehicle model" value={vehicleModel} />
        <Field label="Contact" value={contactNo} mono />
        <Field label="Created by" value={createdBy === 'N/A' ? <span className="text-slate-500">—</span> : createdBy} />
        <Field label="Case created" value={caseCreationDate} />
        <Field
          label="Last service"
          value={
            lastServiceCreationDate === 'N/A' ? (
              <span className="text-slate-500">N/A</span>
            ) : (
              lastServiceCreationDate
            )
          }
        />
      </div>

      {saveMessage ? (
        <div
          className={`mt-4 rounded-lg border p-3 text-sm ${
            saveMessage.includes('successfully')
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {saveMessage}
        </div>
      ) : null}
    </Section>
  );
};

export default memo(CaseDetailsSegment);
